/**
 * components/riskTerminal/columns.js
 *
 * upstox-074 (item 09) — ONE column configuration for the positions track: the header, leg, flat,
 * holdings and closed rows all read it (the widths used to be repeated in five places). When the
 * track — measured with a ResizeObserver on the track itself, not the viewport — is narrower than
 * its content, columns are hidden in this order: INTENT (60) → CHARGES (84) → STOP (140). LTP and
 * P&L are always visible, and the track never scrolls sideways.
 */

import { createContext, useContext, useEffect, useState } from 'react';

export const COL = { intent: 60, qty: 56, avg: 70, ltp: 70, pl: 110, charges: 84, stop: 140, actions: 60, caret: 22, pad: 28 };
export const NAME_MIN = 190;                       // the name column never gets less than this
export const HIDE_ORDER = ['intent', 'charges', 'stop'];

/** The columns shown at a track width: { intent, charges, stop } booleans (the rest always show). */
export function visibleColumns(trackWidth, extra = 0) {
  const vis = { intent: true, charges: true, stop: true };
  if (!trackWidth) return vis;
  const need = () => extra + COL.pad + COL.caret + NAME_MIN + COL.qty + COL.avg + COL.ltp + COL.pl + COL.actions
    + (vis.intent ? COL.intent : 0) + (vis.charges ? COL.charges : 0) + (vis.stop ? COL.stop : 0);
  for (const k of HIDE_ORDER) { if (need() > trackWidth) vis[k] = false; }
  return vis;
}

export const ColsCtx = createContext({ intent: true, charges: true, stop: true });
export const useCols = () => useContext(ColsCtx);

/** Width in px of a column's cell style. */
export const cw = k => COL[k] + 'px';
export const cell = (k, extra) => ({ width: cw(k), flex: '0 0 ' + cw(k), ...extra });

/** Observe the track element (held in state, so it is picked up when it mounts) and return which columns fit. */
export function useTrackColumns(el, extra = 0) {
  const [vis, setVis] = useState(() => visibleColumns(null));
  useEffect(() => {
    if (!el) return undefined;
    const measure = () => {
      const next = visibleColumns(el.clientWidth, extra);
      setVis(v => (v.intent === next.intent && v.charges === next.charges && v.stop === next.stop ? v : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, extra]);
  return vis;
}
